import React from "react";
import _ from "lodash";
import { Grid } from "@mui/material";
import { useCurrentEditor } from "@tiptap/react";
import ActivityFormInner from "./ActivityFormInner";
import SubmitButton from "../../Button/SubmitButton";
import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import useForm, { UseFormProps } from "../../../hooks/useForm";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { createPortal } from "react-dom";
import { ProductUnion } from "../../../types/ProductUnion";
import { Activity } from "../../../types/Activity";
import { useOptionalDrawer } from "../../../provider/DrawerProvider";
import { UseForm } from "../../../types/UseForm";
import useDraftAutosave from "../../../hooks/useDraftAutosave";
import useEffectWithoutMount from "../../../hooks/useEffectWithoutMount";
import DraftAutosaveIndicator from "../../GlobalDrawer/DraftAutosaveIndicator";

export type ActivityFormProps = {
  initialValues: UseFormProps["initialValues"];
  onSubmit: UseFormProps["onSubmit"];
  closeDialog: Function;
  product?: ProductUnion;
  productClassName: string;
  activity?: Activity;
  open: boolean;
  title?: string;
  renderAsModal?: boolean;
  autosave?: (values: UseForm["values"]) => Promise<void>;
};

const ActivityForm = ({
  renderAsModal = false,
  title,
  initialValues,
  onSubmit,
  closeDialog,
  product,
  productClassName,
  activity,
  open,
  autosave,
}: ActivityFormProps) => {
  const { editor } = useCurrentEditor();

  const formProps = useForm({
    initialValues,
    onSubmit,
    identifier: !!activity ? activity : "new",
    clearFormAfterSubmit: true,
    disableValidation: !!autosave,
  });

  const { handleSubmit, errors, touchedValues, values, clearForm } = formProps;
  const drawerContext = useOptionalDrawer();

  const { saveState, scheduleSave } = useDraftAutosave({
    enabled: !!autosave,
    persist: async () => {
      await autosave?.(values);
    },
    editor,
  });

  useEffectWithoutMount(() => scheduleSave(), [values]);

  const internalCloseDialog = () => {
    clearForm();
    closeDialog();
  };

  const innerFormProps = {
    formProps,
    product,
    productClassName,
    activity,
    open,
  };

  const isSubmitDisabled =
    (_.keys(touchedValues).length === 0 && getEditorHtml(editor) === activity?.note) ||
    values.type === "no_type" ||
    !values.assignedUser;

  const submitButtonProps = {
    onClick: handleSubmit,
    variant: "contained",
    error: _.keys(errors).length > 0,
    disabled: isSubmitDisabled,
  };

  if (renderAsModal) {
    return (
      <LegalbirdIoModal
        handleClose={internalCloseDialog}
        open={open}
        disableBackdropClick
        title={title || "Aktivität"}
        submitButton={<SubmitButton {...submitButtonProps}>Speichern</SubmitButton>}
      >
        <ActivityFormInner {...innerFormProps} />
      </LegalbirdIoModal>
    );
  }

  if (!drawerContext?.drawerActionsNode) {
    return null;
  }

  return (
    <>
      <ActivityFormInner {...innerFormProps} />
      {autosave &&
        drawerContext.drawerHeaderNode &&
        createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerContext.drawerHeaderNode)}
      {!autosave &&
        createPortal(
          <Grid container sx={{ width: "100%" }}>
            <Grid size={3} sx={{ display: { xs: "none", md: "block" } }} />
            <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: 6 }}>
              <SubmitButton {...submitButtonProps}>Speichern</SubmitButton>
            </Grid>
          </Grid>,
          drawerContext.drawerActionsNode,
        )}
    </>
  );
};

export default ActivityForm;
